import React, { useEffect, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom"; // ✅ Import for URL sync
import { toast } from "../../components/ui/use-toast";
import XanoAPI from "../../lib/XanoAPI";
import KanbanColumn from "./kanban-column";
import KanbanCard from "./kanban-card";
import { DataTableToolbar } from "../table/data-table-toolbar";
import { useReactTable, getCoreRowModel } from "@tanstack/react-table";
import { ClientType } from "../../types";
import { useAuth } from "../../lib/AuthContext";

// ✅ Define lifecycle stages explicitly
const LIFECYCLE_STAGES: NonNullable<ClientType["lifecycleStage"]>[] = [
  "prospect",
  "initial_outreach",
  "benefits_analysis",
  "proposal_sent",
  "customer",
  "lost",
];

// Stage labels for display
const STAGE_LABELS: Record<
  NonNullable<ClientType["lifecycleStage"]>,
  string
> = {
  prospect: "Prospect",
  initial_outreach: "Initial Outreach",
  benefits_analysis: "Benefits Analysis",
  proposal_sent: "Proposal Sent",
  customer: "Customer",
  lost: "Lost",
};

const KanbanBoard: React.FC = () => {
  const api = XanoAPI.getInstance();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // ✅ URL state management
  const auth = useAuth();

  // ✅ Check if current user is admin
  const isAdmin = auth?.currentUser?.user_permissions?.[0]?.role === "admin";

  // ✅ Extract filters from URL
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || ""
  );
  const [lifecycleStage, setLifecycleStage] = useState(
    searchParams.get("lifecycle") || ""
  );
  const [agencyFilter, setAgencyFilter] = useState(
    searchParams.get("agency") || ""
  );
  const [stateFilter, setStateFilter] = useState(
    searchParams.get("state") || ""
  );
  const [userFilter, setUserFilter] = useState(
    searchParams.get("userId") || ""
  );

  // State for active dragging
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeClient, setActiveClient] = useState<ClientType | null>(null);

  // Configure DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  // ✅ Fetch Agents (for admin user filter dropdown)
  const { data: agentsData } = useQuery({
    queryKey: ["agents"],
    queryFn: async () => {
      if (!isAdmin) return [];
      try {
        const response = await api.getUsersByAgencyId(
          auth?.currentUser?.agency_id || 0
        );
        console.log("✅ Fetched agency response:", response);

        // Extract members from the agency object
        // API returns array of agencies, we need the first one's members
        const members = response?.[0]?.members || [];
        console.log("✅ Extracted members:", members);

        return members;
      } catch (error) {
        console.error("❌ Error fetching agents:", error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: isAdmin, // Only fetch if user is admin
  });

  // ✅ Sync filters with URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    if (lifecycleStage) params.set("lifecycle", lifecycleStage);
    if (agencyFilter) params.set("agency", agencyFilter);
    if (stateFilter) params.set("state", stateFilter);
    if (userFilter) params.set("userId", userFilter);

    navigate({ search: params.toString() }, { replace: true }); // ✅ Update URL
  }, [
    search,
    statusFilter,
    lifecycleStage,
    agencyFilter,
    stateFilter,
    userFilter,
    navigate,
  ]);

  // ✅ Fetch clients from API
  const { data, isLoading, error } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => {
      const response = await api.getAllClients(1, "", 100, {});
      return response.items || [];
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // ✅ State for managing clients grouped by lifecycleStage
  const [clientsByStage, setClientsByStage] = useState<
    Record<NonNullable<ClientType["lifecycleStage"]>, ClientType[]>
  >(() =>
    LIFECYCLE_STAGES.reduce(
      (acc, stage) => ({ ...acc, [stage]: [] }),
      {} as Record<NonNullable<ClientType["lifecycleStage"]>, ClientType[]>
    )
  );

  // ✅ Filter and group clients by lifecycleStage
  useEffect(() => {
    if (data?.length) {
      const filteredClients = data.filter((client: ClientType) => {
        return (
          (!search ||
            client.lastName?.toLowerCase().includes(search.toLowerCase())) &&
          (!statusFilter || client.status === statusFilter) &&
          (!lifecycleStage || client.lifecycleStage === lifecycleStage) &&
          (!agencyFilter || client.federalAgency === agencyFilter) &&
          (!stateFilter || client.stateProvince === stateFilter)
        );
      });

      const groupedClients = LIFECYCLE_STAGES.reduce(
        (acc, stage) => {
          acc[stage] = filteredClients.filter(
            (client: ClientType) => client.lifecycleStage === stage
          );
          return acc;
        },
        {} as Record<NonNullable<ClientType["lifecycleStage"]>, ClientType[]>
      );

      setClientsByStage(groupedClients);
    }
  }, [data, search, statusFilter, lifecycleStage, agencyFilter, stateFilter]);

  // Handle drag start
  const handleDragStart = (event: any) => {
    const { active } = event;
    const activeClientId = String(active.id);

    // Find the client being dragged
    const client = Object.values(clientsByStage)
      .flat()
      .find((c) => String(c.id) === activeClientId);

    if (client) {
      setActiveId(activeClientId);
      setActiveClient(client);
    }
  };

  // Handle drag & drop
  const handleDragEnd = useCallback(
    async (event: any) => {
      const { active, over } = event;

      setActiveId(null);
      setActiveClient(null);

      if (!active || !over) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      const activeClient = Object.values(clientsByStage)
        .flat()
        .find((c) => String(c.id) === activeId);

      if (!activeClient || !activeClient.lifecycleStage) return;

      const activeStage = activeClient.lifecycleStage;
      const overStage =
        LIFECYCLE_STAGES.find((stage) => stage === overId) ||
        (Object.keys(clientsByStage).find((stage) =>
          clientsByStage[
            stage as NonNullable<ClientType["lifecycleStage"]>
          ].some((c) => String(c.id) === overId)
        ) as NonNullable<ClientType["lifecycleStage"]>);

      if (!overStage || activeStage === overStage) return;

      // Store previous state for potential reversion
      const previousClientsByStage = { ...clientsByStage };

      // Update local state immediately (optimistic update)
      setClientsByStage((prev) => {
        const updatedActiveStage = prev[activeStage].filter(
          (c) => String(c.id) !== activeId
        );
        const updatedOverStage = [
          ...prev[overStage],
          {
            ...activeClient,
            lifecycleStage: overStage,
            last_lifecycle_update: new Date().toISOString(),
          },
        ];

        return {
          ...prev,
          [activeStage]: updatedActiveStage,
          [overStage]: updatedOverStage,
        };
      });

      // Update lifecycle stage in API
      if (activeClient?.id) {
        try {
          await api.updateClient(activeClient.id, {
            ...activeClient,
            lifecycleStage: overStage,
            last_lifecycle_update: new Date().toISOString(),
          });

          toast({
            title: "Client moved",
            description: `Client moved to ${STAGE_LABELS[overStage]}`,
          });

          // Quietly refresh data in the background
          queryClient.invalidateQueries({ queryKey: ["clients"] });
        } catch (error) {
          console.error("Failed to update client stage:", error);

          // Revert to previous state if API call fails
          setClientsByStage(previousClientsByStage);

          toast({
            title: "Error moving client",
            description: "The client could not be moved. Please try again.",
            variant: "destructive",
          });
        }
      }
    },
    [clientsByStage, api, queryClient]
  );

  // Handle drag over event for visual feedback
  const handleDragOver = (event: any) => {
    // We can add additional visual cues here if needed
  };

  // Define columns for React Table
  const columns = React.useMemo(
    () => [
      { accessorKey: "firstName", header: "First Name" },
      { accessorKey: "lastName", header: "Last Name" },
      { accessorKey: "email", header: "Email" },
      { accessorKey: "lifecycleStage", header: "Stage" },
    ],
    []
  );

  // Create Table instance
  const table = useReactTable({
    data: data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return <div className="text-center p-4">Loading clients...</div>;
  if (error)
    return <div className="text-red-500 p-4">Error fetching clients</div>;

  // If no clients match filter criteria
  const totalClients = Object.values(clientsByStage).flat().length;
  if (totalClients === 0 && data && data.length > 0) {
    return (
      <div>
        <DataTableToolbar
          table={table}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          lifecycleStage={lifecycleStage}
          setLifecycleStage={setLifecycleStage}
          agencyFilter={agencyFilter}
          setAgencyFilter={setAgencyFilter}
          stateFilter={stateFilter}
          setStateFilter={setStateFilter}
          userFilter={userFilter}
          setUserFilter={setUserFilter}
          agents={agentsData || []}
          isAdmin={isAdmin}
        />
        <div className="p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900">
            No clients match your filters
          </h3>
          <p className="text-gray-500 mt-1">
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              setLifecycleStage("");
              setAgencyFilter("");
              setStateFilter("");
              setUserFilter("");
            }}
            className="mt-4 text-primary hover:text-primary/80"
          >
            Clear all filters
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ✅ Include Toolbar for Filtering */}
      <DataTableToolbar
        table={table} // ✅ Pass actual table instance
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        lifecycleStage={lifecycleStage}
        setLifecycleStage={setLifecycleStage}
        agencyFilter={agencyFilter}
        setAgencyFilter={setAgencyFilter}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        userFilter={userFilter}
        setUserFilter={setUserFilter}
        agents={agentsData || []}
        isAdmin={isAdmin}
      />

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
      >
        <div className="flex space-x-4 overflow-x-auto p-4 w-full">
          {LIFECYCLE_STAGES.map((stage) => (
            <SortableContext
              key={stage}
              items={clientsByStage[stage].map((c) => String(c.id))}
              strategy={verticalListSortingStrategy}
            >
              <KanbanColumn
                stage={stage}
                clients={clientsByStage[stage] ?? []}
                className="transition-transform duration-300 ease-in-out"
              />
            </SortableContext>
          ))}
        </div>

        {/* Drag Overlay for better visual feedback */}
        <DragOverlay>
          {activeId && activeClient && (
            <div className="opacity-80 transform scale-105 z-50 rounded-md shadow-xl">
              <KanbanCard id={activeId} client={activeClient} />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default KanbanBoard;
